import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import type { TenantSettingsJson } from "@/types/tenantSettings";

export interface BrokerTenant {
  id: number;
  slug: string;
  name: string;
  domain: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  defaultLanguage: string | null;
  defaultCurrency: string | null;
  defaultTimezone: string | null;
  isPlatform: boolean;
  status: string;
  /** Public-safe slice of tenants.settings — see backend tenant-public.ts. */
  settings?: TenantSettingsJson;
}

export interface CurrentTenantResponse {
  subdomain: string | null;
  tenant: BrokerTenant | null;
}

/**
 * Resolves which broker the trader landed on. Backed by
 * `GET /tenant/current`, which reads the request's host (forwarded by
 * the Vite proxy as `X-Forwarded-Host`) and looks up the matching slug.
 *
 * When the user hits plain `localhost:3019` with no subdomain, this
 * returns `{ subdomain: null, tenant: null }` and the UI falls back to
 * generic platform branding.
 */
export function useCurrentTenant() {
  return useQuery({
    queryKey: ["tenant-current"],
    queryFn: async () =>
      unwrap<CurrentTenantResponse>((await api.get("/tenant/current")).data),
    staleTime: 5 * 60 * 1000, // host doesn't change mid-session
    refetchOnWindowFocus: false,
  });
}
